import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Loader2, AlertCircle, Search } from "lucide-react";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const defaultCenter = { lat: 25.276987, lng: 55.296249 };

function safeLatLng(obj) {
  let lat = Number(obj?.lat);
  let lng = Number(obj?.lng);
  if (!isFinite(lat)) lat = defaultCenter.lat;
  if (!isFinite(lng)) lng = defaultCenter.lng;
  return { lat, lng };
}

const MapPicker = ({
  onLocationSelect,
  initialLocation = defaultCenter,
  apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  className = "",
  placeholder = "Search for a location...",
}) => {
  const mapRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [mapCenter, setMapCenter] = useState(safeLatLng(initialLocation));
  const [markerPosition, setMarkerPosition] = useState(safeLatLng(initialLocation));
  const [searchQuery, setSearchQuery] = useState(initialLocation.address || "");
  const [currentAddress, setCurrentAddress] = useState(initialLocation.address || "");
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });

  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!window.google || !window.google.maps) return `Pinned Location: ${lat}, ${lng}`;
    setIsFetchingAddress(true);

    const address = await new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      let resolved = false;

      geocoder.geocode({ location: { lat, lng } }, async (results, status) => {
        if (resolved) return;
        resolved = true;
        if (status === "OK" && results[0]) return resolve(results[0].formatted_address);

        // fallback to nearby place
        const service = new window.google.maps.places.PlacesService(document.createElement("div"));
        const request = { location: { lat, lng }, radius: 50, rankBy: window.google.maps.places.RankBy.DISTANCE };
        service.nearbySearch(request, (places, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && places.length) {
            resolve(places[0].name);
          } else {
            resolve(`Pinned Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        });
      });

      // Safety timeout
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(`Pinned Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
      }, 5000);
    });

    setIsFetchingAddress(false);
    return address;
  }, []);

  const handleLocationChange = useCallback(
    async (lat, lng, addressFromAutocomplete) => {
      const safeLoc = safeLatLng({ lat, lng });
      setMarkerPosition(safeLoc);
      setMapCenter(safeLoc);

      const address = addressFromAutocomplete || (await reverseGeocode(safeLoc.lat, safeLoc.lng));
      setCurrentAddress(address);
      setSearchQuery(address);

      if (onLocationSelect) {
        onLocationSelect({ latitude: safeLoc.lat, longitude: safeLoc.lng, location: address });
      }
    },
    [onLocationSelect, reverseGeocode]
  );

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      map.addListener("click", (event) => {
        handleLocationChange(event.latLng.lat(), event.latLng.lng());
      });

      if (initialLocation.lat && initialLocation.lng) {
        handleLocationChange(initialLocation.lat, initialLocation.lng, initialLocation.address);
      }
    },
    [handleLocationChange, initialLocation]
  );

  const onMarkerDragEnd = useCallback(
    (e) => {
      handleLocationChange(e.latLng.lat(), e.latLng.lng());
    },
    [handleLocationChange]
  );

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    if (autocompleteInputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
        types: ["geocode", "establishment"],
        componentRestrictions: { country: "ae" },
        fields: ["formatted_address", "geometry", "name", "place_id"],
      });
      autocompleteRef.current.addListener("place_changed", async () => {
        const place = autocompleteRef.current.getPlace();
        if (!place?.geometry?.location) return;
        handleLocationChange(
          place.geometry.location.lat(),
          place.geometry.location.lng(),
          place.formatted_address || place.name
        );
      });
    }
  }, [isLoaded, handleLocationChange]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setError(null);
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery, componentRestrictions: { country: "ae" } }, async (results, status) => {
      setIsSearching(false);
      if (status === "OK" && results[0]) {
        const location = results[0];
        handleLocationChange(location.geometry.location.lat(), location.geometry.location.lng(), location.formatted_address);
      } else {
        setError("Location not found");
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch(e);
  };

  if (loadError) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
        <AlertCircle className="inline mr-2" size={16} />
        Error loading Google Maps
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md">
        <Loader2 className="animate-spin text-blue-500 mr-2" size={20} />
        Loading map...
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Input */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            ref={autocompleteInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="w-full p-3 pr-10 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isSearching}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500"
          >
            {isSearching ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="relative rounded-lg overflow-hidden border border-gray-300 shadow-sm">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={14}
          onLoad={onMapLoad}
          options={{ streetViewControl: false, zoomControl: true, fullscreenControl: true, gestureHandling: "cooperative" }}
        >
          <Marker position={markerPosition} draggable onDragEnd={onMarkerDragEnd} animation={window.google?.maps?.Animation?.DROP} />
        </GoogleMap>
      </div>

      {/* Selected Location */}
     
    </div>
  );
};

export default MapPicker;
